import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";

const StyledSnackbar = styled(Snackbar)(({ theme, success, error }) => ({
  "& .MuiSnackbarContent-root": {
    backgroundColor: success
      ? theme.palette.success.main || theme.palette.primary.main
      : theme.palette.error.main,
  },
  "& .MuiSnackbarContent-message": {
    display: "flex",
    alignItems: "center",
    color: success
      ? theme.palette.success.contrastText || theme.palette.primary.main
      : theme.palette.error.contrastText || theme.palette.secondary.main,
  },
  "& .MuiSnackbarContent-action": {
    "& .MuiIconButton-root": {
      color: success
        ? theme.palette.success.contrastText || theme.palette.primary.main
        : theme.palette.error.contrastText || theme.palette.secondary.main,
    },
  },
}));

const StyledIcon = styled("span")(({ theme }) => ({
  fontSize: 20,
  opacity: 0.9,
  marginRight: "15px",
}));

/**
 * Snackbar que recebe parametros:
 *  @param{} success o snackbar será mostrado na cor verde
 *  @param{} error o snackbar será mostrado na cor vermelho
 *  @param{} duration parametro opcional que indicara o tempo o Snackbar ficará visivel (o default é 6 segundos)
 *  @param{} message mensagem que atechnicalAdviceá no Snackbar
 *  @param{} handleClose método que será chamado após o Snackbar fechar
 *  @param{} errorWithChildren o snackbar será mostrado na cor vermelho,
 *  e tambem torna possivel passar componentes no campo message, (e.g.,\<div>My message\</div>) ao invés de uma simples String
 */
const GenericSnackbar = ({
  success,
  error,
  errorWithChildren,
  message,
  duration,
  handleClose,
}) => {
  const [openSnack, setOpenSnack] = useState(true);

  useEffect(() => {
    setOpenSnack(true);
  }, [success, error]);

  const handleSnackClose = (event, reason) => {
    setOpenSnack(false);
    handleClose();
  };

  const renderMessage = () => {
    if (success) {
      return (
        <span id="client-snackbar">
          <StyledIcon>
            <CheckCircleIcon />
          </StyledIcon>
          {message}
        </span>
      );
    }

    if (error) {
      return (
        <span id="client-snackbar">
          <StyledIcon>
            <ErrorIcon />
          </StyledIcon>
          {message}
        </span>
      );
    }

    if (errorWithChildren) {
      return (
        <div id="client-snackbar">
          <StyledIcon>
            <ErrorIcon />
          </StyledIcon>
          {message}
        </div>
      );
    }

    return null;
  };

  return (
    <StyledSnackbar
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      open={openSnack}
      autoHideDuration={duration || 6000}
      onClose={handleSnackClose}
      success={success}
      error={error}
      message={renderMessage()}
      action={[
        <IconButton
          key="close"
          aria-label="Close"
          color="secondary"
          onClick={handleSnackClose}
        >
          <CloseIcon />
        </IconButton>,
      ]}
    />
  );
};

GenericSnackbar.propTypes = {
  success: PropTypes.bool,
  error: PropTypes.bool,
  errorWithChildren: PropTypes.bool,
  message: PropTypes.node,
  duration: PropTypes.number,
  handleClose: PropTypes.func.isRequired,
};

export default GenericSnackbar;
